import * as React from 'react';
import { SortableElement, SortableElementProps } from 'react-sortable-hoc';

import SidebarItem from '@components/basics/sidebar-item';
import SidebarItemSong from '@components/basics/sidebar-item/sidebar-item-song';

import { trackSection } from '@webapp/utils/validators/release';
import { datesSection } from '../../../utils/validators/release';
import AppState from '@webapp/store/state';
import { connect } from 'react-redux';
import { generalStatus } from '../../../store/assets/selectors';

import { trackList } from '@webapp/store/tracks/selectors';
import { collaboratorsList } from '@webapp/store/collaborator/selectors';
import { getOwnershipTotal } from '@webapp/store/ownership/selectors';
import { isISRC } from '@webapp/utils/validators';

interface TrackSidebarItemProps {
  trackId: string;
  title: string;
  order?: number;

  isUploading: boolean;
  isInstrumental: boolean;
  isExplicit: boolean;
  isClean: boolean;
  isInstantGratification: boolean;
  isNFTExlusive?: boolean;
  hasErrors: boolean;

  progress: number;
  artistId?: string;
  releaseId?: string;
  location: any;
  onRemove: (trackId: string, isActive: boolean) => void;

  disableRemove?: boolean;
  routePrefix?: string;
  hideRemoveOption?: boolean;
  disableSorting?: boolean;
  hasErrorField?: boolean;
}

interface StateProps {
  isDone: boolean;
  isDisabled: boolean;
  isLoading: boolean;
}

const mapStateToProps = (state: AppState, props: TrackSidebarItemProps) => {
  const asset = state.assets.statuses[props.trackId];
  const currentTrack = state.tracks.tracks[props.trackId];
  const isSong = currentTrack?.type === 'song';
  const isLoading = generalStatus(state.assets) === 'uploading';

  const invalidForm = () => {
    const isExplicit = currentTrack?.id
      ? state.tracks.tracks[currentTrack.id].isExplicit
      : undefined;
    const tracksWithCurrentIsrc = trackList(
      state.tracks.tracks,
      state.release.release?.id
    ).filter((track) => track.isrc === currentTrack?.isrc);
    const conditions = [
      //missingArtists
      !collaboratorsList(state.collaborator, props.trackId).length,
      //missingMainArtist
      !collaboratorsList(state.collaborator, props.trackId).find(
        (a) => a.role === 'main'
      ),
      //missingOwners
      getOwnershipTotal(state.ownership, props.trackId, 'OWNER') !== 100,
      //missingSongwriters
      getOwnershipTotal(state.ownership, props.trackId, 'WRITER') !== 100,
      //missingVideo
      currentTrack?.hasMusicVideo && !currentTrack?.video?.videoAssetId,
      //missingPoster
      currentTrack?.hasMusicVideo && !currentTrack?.video?.coverAssetId,
      //missingLanguage
      isSong ? !currentTrack?.language : false,
      //missingType
      currentTrack?.type === undefined,
      //isExplicit
      isExplicit === undefined && isSong,
      // invalidIsrc
      currentTrack?.isrc ? !isISRC(currentTrack?.isrc) : false,
      //repeatedIsrc
      currentTrack?.isrc ? tracksWithCurrentIsrc.length > 1 : false,
      // missingSongName
      !currentTrack?.title,
    ];

    return conditions.some((cond) => cond === true);
  };
  return {
    hasErrorField: state.tracks.invalidForm ? invalidForm() : false,
    isLoading: !asset?.validationDone,
    isDone: trackSection(state, props.trackId),
    isDisabled: !datesSection(state) || isLoading,
  };
};

export const TrackSidebarItem: React.FC<TrackSidebarItemProps & StateProps> = (
  props
) => {
  const isActive = (trackId: string) => {
    if (!props.location.pathname.startsWith('/release/')) {
      return false;
    }

    return props.location.pathname.endsWith(`track/${trackId}`);
  };

  const getRoute = (trackId: string) => {
    return (
      '/' +
      [
        'release',
        props.artistId,
        props.releaseId,
        props.routePrefix ? props.routePrefix : 'track',
        trackId,
      ]
        .filter((f) => !!f)
        .join('/')
    );
  };

  return (
    <SidebarItem
      route={getRoute(props.trackId)}
      active={isActive(props.trackId)}
      done={props.isDone}
      disabled={props.isDisabled}
      hideLineOnHover
      error={props.hasErrors || props.hasErrorField}
    >
      <SidebarItemSong
        number={String(props.order || '0').padStart(2, '0')}
        text={props.title}
        active={isActive(props.trackId)}
        done={props.isDone}
        uploading={props.isUploading}
        progress={props.progress}
        isExplicit={props.isExplicit}
        isInstrumental={props.isInstrumental}
        isClean={props.isClean}
        isInstantGratification={props.isInstantGratification}
        isNFTExlusive={props.isNFTExlusive}
        isLoading={props.isLoading}
        error={props.hasErrors || props.hasErrorField}
        onRetry={() => null}
        onRemove={() => props.onRemove(props.trackId, isActive(props.trackId))}
        hideRemoveOption={props.hideRemoveOption}
        disableRemove={props.disableRemove}
        disableSorting={props.disableSorting}
      />
    </SidebarItem>
  );
};

const SortableTrackSideBarItem = SortableElement(TrackSidebarItem);

export default connect(mapStateToProps)(
  (props: TrackSidebarItemProps & StateProps & SortableElementProps) => {
    if (props.disableSorting) {
      return <TrackSidebarItem {...props} />;
    }

    //this hardcoded disable prop is needed for enabling the sort functionality
    //if not it will take the value from the props
    return <SortableTrackSideBarItem {...props} disabled={false} />;
  }
);
